import { apiClient } from './client';
import type { Envelope } from './types';

export type ExportFormat = 'csv' | 'json';
export type ImportMode = 'merge' | 'replace';

export interface ImportError {
  line: number;
  code: string;
  message: string;
}

export interface ImportResult {
  valid: number;
  imported: number;
  errors: ImportError[];
  mode: ImportMode;
}

export interface ExportParams {
  format: ExportFormat;
  from?: string;
  to?: string;
}

/**
 * Returns a signed-in browser download by hitting /api/export.
 * (axios blob + a programmatic anchor click — works because the
 * endpoint sets Content-Disposition: attachment.)
 */
export async function downloadExport(params: ExportParams): Promise<void> {
  const res = await apiClient.get<Blob>('/export', {
    params,
    responseType: 'blob',
  });
  const blob = res.data;
  // Try to extract filename from Content-Disposition
  const cd = res.headers['content-disposition'] ?? '';
  const m = /filename="([^"]+)"/.exec(cd);
  const filename = m?.[1] ?? `health-records.${params.format}`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function importRecords(
  file: File,
  mode: ImportMode,
  format: ExportFormat
): Promise<ImportResult> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiClient.post<Envelope<ImportResult>>('/import', formData, {
    params: { mode, format },
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data.data;
}
